/**
 * `<media-gallery>` custom element: the thumbnail rail drives one stage
 * (v1.3) and the colour filter follows the selected variant (v1.2).
 *
 * The Liquid renders every media in the stage and stamps the ones it can
 * match against the DECLARED colour option values (AD-12) with
 * data-media-color, hiding those of the other colours. This element reveals
 * one media at a time, keeps the thumbnails in step (hidden ones included)
 * and, when the colour changes under the active media, falls back to the
 * first one still visible. Without it the rail stays hidden by CSS and the
 * stage shows every photo stacked.
 *
 * Under 750px the CSS turns the track into a scroll-snap strip: the finger
 * moves the photos, this element only follows the scroll to mark the
 * thumbnail and update the `1 / 6` counter, and scrolls the track when a
 * thumbnail is tapped. Above 750px the track never overflows and the scroll
 * listener never fires.
 */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    const stage = this.querySelector('[data-gallery-stage]');
    if (!stage) return;
    this.track = stage.querySelector('[data-gallery-track]') || stage;
    this.counter = this.querySelector('[data-gallery-counter]');
    this.items = Array.from(this.track.querySelectorAll('.media'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!this.items.length) return;

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

    this.classList.add('product-gallery--ready');
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) this.show(thumb.dataset.galleryThumb);
    });

    this.syncThumbs();
    this.showFirstVisible();

    const map = this.querySelector('[data-variant-colors]');
    if (!map) return;
    try {
      this.colors = JSON.parse(map.textContent);
    } catch (error) {
      return;
    }
    document.addEventListener('shopify:product:select', (event) => this.onVariant(event));
  }

  show(id, fromScroll = false) {
    const target = this.items.find((media) => media.dataset.mediaId === id);
    if (!target) return;
    this.active = target;
    this.items.forEach((media) => media.classList.toggle('is-active', media === target));
    this.thumbs.forEach((thumb) =>
      thumb.setAttribute('aria-current', thumb.dataset.galleryThumb === id ? 'true' : 'false')
    );
    // Sopra i 750 la pista non scorre e offsetLeft è 0: la chiamata è innocua.
    if (!fromScroll) this.track.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
    this.updateCounter();
  }

  visibleItems() {
    return this.items.filter((media) => !media.hidden);
  }

  updateCounter() {
    if (!this.counter) return;
    const visible = this.visibleItems();
    const index = visible.indexOf(this.active);
    this.counter.hidden = visible.length < 2 || index < 0;
    if (!this.counter.hidden) this.counter.textContent = `${index + 1} / ${visible.length}`;
  }

  /** Quale slide è a schermo: la pista è larga una slide, basta dividere. */
  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const width = this.track.clientWidth;
      if (!width) return;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      const media = this.visibleItems()[index];
      if (media && media !== this.active) this.show(media.dataset.mediaId, true);
    });
  }

  showFirstVisible() {
    const first = this.items.find((media) => !media.hidden);
    if (first) this.show(first.dataset.mediaId);
  }

  /**
   * Events for another product (the quick view modal) miss the map: ignored.
   * Il payload degli eventi standard sta SULL'EVENTO, non in `event.detail`:
   * leggerlo da lì significa non reagire mai.
   */
  onVariant(event) {
    const id = event.variant ? String(event.variant.id) : null;
    if (!id || !(id in this.colors)) return;
    const color = this.colors[id];
    this.items.forEach((media) => {
      if (media.dataset.mediaColor) media.hidden = media.dataset.mediaColor !== color;
    });
    this.syncThumbs();
    if (!this.active || this.active.hidden) this.showFirstVisible();
    // Il colore nuovo può avere più o meno foto: il totale del contatore cambia.
    this.updateCounter();
  }

  /** Same Liquid loop, same order: the thumbnail of index i belongs to item i. */
  syncThumbs() {
    this.thumbs.forEach((thumb, index) => {
      if (this.items[index]) thumb.hidden = this.items[index].hidden;
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
